import React, { useState } from "react";
import { Plus, Trash2, ChevronRight, ChevronLeft, Check, Zap, Database, Layers, Globe, Code2, GitBranch } from "lucide-react";

const STEP_ICONS = [Database, Layers, GitBranch, Globe];
const STEP_LABELS = ["Entidad Base", "CDS Stack", "Behavior", "Service"];
const STEP_SUBLABELS = ["Tabla + campos clave", "ZI_ + ZC_ + Assoc.", "CRUD + Draft + Actions", "OData V2/V4"];

const FIELD_TYPES = ["abap.char(10)", 
    "abap.char(40)", 
    "abap.char(100)", 
    "abap.int4", 
    "abap.int8", 
    "abap.dec(15,2)", 
    "abap.dats", 
    "abap.tims", 
    "abap.boolean", 
    "abap.uuid", 
    "bukrs", 
    "gjahr", 
    "waers", 
    "kunnr", 
    "lifnr", 
    "matnr", 
    "pernr", 
    "vbeln", 
    "belnr_d"];
const TEMPLATES = [
    { id: "managed", label: "Managed", icon: "⚡", desc: "Framework maneja CRUD automáticamente. Recomendado para nuevos desarrollos." },
    { id: "unmanaged", label: "Unmanaged", icon: "🔧", desc: "Control total de la lógica. Para casos complejos con tablas legacy." },
    { id: "draft", label: "Draft-Enabled", icon: "📝", desc: "Soporte para borradores. Ideal para formularios de entrada de datos." },
];
const BINDING_TYPES = [
    { id: "v4", label: "OData V4", badge: "Recomendado", color: "emerald" },
    { id: "v2", label: "OData V2", badge: "Legacy", color: "amber" },
];
// Rap Wizard — Asistente para generación de objetos RAP a partir de una tabla base y campos clave, 
// con opciones para template, asociaciones, operaciones CRUD, draft handling y tipo de service binding. 
// Genera código boilerplate listo para usar en ADT.
const initialState = {
    entityName: "",
    sourceTable: "",
    template: "managed",
    keyFields: [{ name: "Id", type: "abap.uuid", isKey: true }],
    dataFields: [{ name: "", type: "abap.char(40)" }],
    ziName: "",
    zcName: "",
    sqlViewZI: "",
    sqlViewZC: "",
    associations: [],
    operations: { create: true, update: true, delete: true, read: true },
    draftEnabled: false,
    actions: [],
    validations: [],
    determinations: [],
    serviceDefName: "",
    serviceBindingName: "",
    bindingType: "v4",
    exposeAssociations: true,
    zTableName: "",
    zDraftTableName: "",
};

const Badge = ({ children, color = "blue" }) => {
    const colors = {
        blue: "bg-blue-500/15 text-blue-400 border-blue-500/30",
        emerald: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
        amber: "bg-amber-500/15 text-amber-400 border-amber-500/30",
        violet: "bg-violet-500/15 text-violet-400 border-violet-500/30",
        rose: "bg-rose-500/15 text-rose-400 border-rose-500/30",
    };
    return <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border ${colors[color]}`}>{children}</span>;
};

const Label = ({ children }) => (
    <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.15em] mb-2">{children}</p>
);

const Input = ({ value, onChange, placeholder, className = "" }) => (
    <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full bg-black/40 border border-white/10 rounded-xl px-4 py-2.5 text-[12px] text-white font-mono placeholder:text-white/20 outline-none focus:border-blue-500/50 transition-all ${className}`}
    />
);

const TypeSelect = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button
                onClick={() => setOpen(!open)}
                className="bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-[11px] text-blue-400 font-mono flex items-center gap-1.5 hover:border-blue-500/40 transition-all whitespace-nowrap"
            >
                {value}
                <ChevronRight size={10} className={`transition-transform shrink-0 ${open ? "rotate-90" : ""}`} />
            </button>
            {open && (
                <div className="absolute top-full left-0 mt-1 bg-[#0B0F1A] border border-white/15 rounded-xl overflow-hidden z-50 shadow-2xl min-w-[160px]">
                    <div className="max-h-48 overflow-y-auto">
                        {FIELD_TYPES.map(t => (
                            <button
                                key={t}
                                onClick={() => { onChange(t); setOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-[11px] font-mono hover:bg-blue-600/20 transition-colors ${value === t ? "text-blue-400 bg-blue-600/10" : "text-white/60"}`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const CardinalidadSelect = ({ value, onChange }) => (
    <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-[12px] text-blue-400 font-mono outline-none focus:border-blue-500/50 transition-all"
        style={{ background: "#0B0F1A" }}
    >
        {["[0..1]", "[0..*]", "[1..1]", "[1..*]"].map(v => (
            <option key={v} value={v} style={{ background: "#0B0F1A" }}>{v}</option>
        ))}
    </select>
);

const Toggle = ({ checked, onChange, label }) => (
    <div className="flex items-center justify-between">
        <span className="text-[12px] text-white/60 font-bold">{label}</span>
        <button
            onClick={() => onChange(!checked)}
            className={`w-12 h-6 rounded-full transition-all duration-300 relative ${checked ? "bg-blue-600" : "bg-white/10"}`}
        >
            <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all duration-300 ${checked ? "left-7" : "left-1"}`} />
        </button>
    </div>
);

const Step1 = ({ data, setData }) => {
    const addField = (type) => {
        const field = { name: "", type: "abap.char(40)", isKey: type === "key" };
        setData(prev => ({
            ...prev,
            [type === "key" ? "keyFields" : "dataFields"]: [...prev[type === "key" ? "keyFields" : "dataFields"], field],
        }));
    };

    const updateField = (type, index, key, value) => {
        const arr = type === "key" ? "keyFields" : "dataFields";
        setData(prev => ({ ...prev, [arr]: prev[arr].map((f, i) => i === index ? { ...f, [key]: value } : f) }));
    };

    const removeField = (type, index) => {
        const arr = type === "key" ? "keyFields" : "dataFields";
        setData(prev => ({ ...prev, [arr]: prev[arr].filter((_, i) => i !== index) }));
    };

    const handleEntityName = (val) => {
        const clean = val.replace(/[^A-Za-z0-9_]/g, "").toUpperCase();
        setData(prev => ({
            ...prev,
            entityName: clean,
            ziName: clean ? `ZI_${clean}` : "",
            zcName: clean ? `ZC_${clean}` : "",
            sqlViewZI: clean ? `ZI${clean.substring(0, 14)}`.substring(0, 16) : "",
            sqlViewZC: clean ? `ZC${clean.substring(0, 14)}`.substring(0, 16) : "",
            serviceDefName: clean ? `ZSD_${clean}` : "",
            serviceBindingName: clean ? `ZSB_${clean}_V4` : "",
            zTableName: clean ? `Z${clean.substring(0, 12)}` : "",
            zDraftTableName: clean ? `Z${clean.substring(0, 10)}_D` : "",
        }));
    };

    return (
        <div className="space-y-6">
            <div>
                <Label>Template RAP</Label>
                <div className="grid grid-cols-3 gap-3">
                    {TEMPLATES.map(t => (
                        <button
                            key={t.id}
                            onClick={() => setData(prev => ({ ...prev, template: t.id, draftEnabled: t.id === "draft" }))}
                            className={`p-4 rounded-2xl border text-left transition-all ${data.template === t.id ? "bg-blue-600/20 border-blue-500/60 shadow-lg shadow-blue-500/10" : "bg-black/20 border-white/8 hover:border-white/20"}`}
                        >
                            <span className="text-xl block mb-2">{t.icon}</span>
                            <p className={`text-[11px] font-black uppercase mb-1 ${data.template === t.id ? "text-blue-400" : "text-white"}`}>{t.label}</p>
                            <p className="text-[10px] text-white/30 leading-tight">{t.desc}</p>
                            {data.template === t.id && <div className="mt-2 text-[8px] font-black uppercase text-blue-500 tracking-widest">● ACTIVO</div>}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label>Nombre de la Entidad</Label>
                    <Input value={data.entityName} onChange={e => handleEntityName(e.target.value)} placeholder="SalesOrder" />
                    <p className="text-[10px] text-white/20 mt-1 font-mono">→ ZI_{data.entityName || "..."} / ZC_{data.entityName || "..."}</p>
                </div>
                <div>
                    <Label>Tabla Fuente (DDIC)</Label>
                    <Input value={data.sourceTable} onChange={e => setData(prev => ({ ...prev, sourceTable: e.target.value.toUpperCase() }))} placeholder="VBAK" />
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-3">
                    <Label>Campos Clave</Label>
                    <button onClick={() => addField("key")} className="flex items-center gap-1.5 text-[10px] font-black text-blue-400 hover:text-blue-300 uppercase tracking-widest transition-colors">
                        <Plus size={12} /> Agregar
                    </button>
                </div>
                <div className="space-y-2">
                    {data.keyFields.map((f, i) => (
                        <div key={i} className="flex gap-2 items-center bg-black/30 border border-white/8 rounded-xl px-3 py-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                            <input value={f.name} onChange={e => updateField("key", i, "name", e.target.value)} placeholder="NombreCampo"
                                className="flex-1 bg-transparent text-[12px] text-white font-mono placeholder:text-white/20 outline-none min-w-0" />
                            <span className="text-white/20 text-[10px]">:</span>
                            <TypeSelect value={f.type} onChange={val => updateField("key", i, "type", val)} />
                            {i > 0 && <button onClick={() => removeField("key", i)} className="text-white/15 hover:text-rose-400 shrink-0"><Trash2 size={12} /></button>}
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-3">
                    <Label>Campos de Datos</Label>
                    <button onClick={() => addField("data")} className="flex items-center gap-1.5 text-[10px] font-black text-blue-400 hover:text-blue-300 uppercase tracking-widest transition-colors">
                        <Plus size={12} /> Agregar
                    </button>
                </div>
                <div className="space-y-2">
                    {data.dataFields.map((f, i) => (
                        <div key={i} className="flex gap-2 items-center bg-black/30 border border-white/8 rounded-xl px-3 py-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                            <input value={f.name} onChange={e => updateField("data", i, "name", e.target.value)} placeholder="NombreCampo"
                                className="flex-1 bg-transparent text-[12px] text-white font-mono placeholder:text-white/20 outline-none min-w-0" />
                            <span className="text-white/20 text-[10px]">:</span>
                            <TypeSelect value={f.type} onChange={val => updateField("data", i, "type", val)} />
                            <button onClick={() => removeField("data", i)} className="text-white/15 hover:text-rose-400 shrink-0"><Trash2 size={12} /></button>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4 space-y-3">
                <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest flex items-center gap-2">
                    <Database size={12} /> Tablas Z (SE11)
                </p>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label>Tabla Z Principal</Label>
                        <Input value={data.zTableName || ""} onChange={e => setData(prev => ({ ...prev, zTableName: e.target.value.toUpperCase() }))}
                            placeholder={data.entityName ? `Z${data.entityName.substring(0, 12)}` : "ZMYTABLE"} />
                    </div>
                    {data.draftEnabled || data.template === "draft" ? (
                        <div>
                            <Label>Tabla Z Draft</Label>
                            <Input value={data.zDraftTableName || ""} onChange={e => setData(prev => ({ ...prev, zDraftTableName: e.target.value.toUpperCase() }))}
                                placeholder={data.entityName ? `Z${data.entityName.substring(0, 10)}_D` : "ZMYTABLE_D"} />
                        </div>
                    ) : (
                        <div className="border border-dashed border-white/10 rounded-xl p-3 flex items-center justify-center">
                            <p className="text-[10px] text-white/20">Activá Draft para tabla Z Draft</p>
                        </div>
                    )}
                </div>
                <p className="text-[10px] text-white/25 italic">El motor generará el DDL de ambas tablas listo para SE11.</p>
            </div>
        </div>
    );
};

const Step2 = ({ data, setData }) => {
    const addAssociation = () => {
        setData(prev => ({ ...prev, associations: [...prev.associations, { name: "", target: "", cardinality: "[0..1]", localField: "", targetField: "" }] }));
    };

    const updateAssoc = (index, key, value) => {
        setData(prev => ({ ...prev, associations: prev.associations.map((a, i) => i === index ? { ...a, [key]: value } : a) }));
    };

    return (
        <div className="space-y-6">
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 rounded-lg bg-blue-600/30 border border-blue-500/40 flex items-center justify-center">
                        <span className="text-[10px] font-black text-blue-400">ZI</span>
                    </div>
                    <p className="text-[12px] font-black text-blue-400 uppercase tracking-widest">Interface View</p>
                    <Badge color="blue">VDM #BASIC</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label>Nombre CDS (ZI_)</Label>
                        <Input value={data.ziName} onChange={e => setData(prev => ({ ...prev, ziName: e.target.value.toUpperCase() }))} placeholder="ZI_SalesOrder" />
                    </div>
                    <div>
                        <Label>SQL View Name (max 16 chars)</Label>
                        <Input value={data.sqlViewZI} onChange={e => setData(prev => ({ ...prev, sqlViewZI: e.target.value.toUpperCase().substring(0, 16) }))} placeholder="ZISALESORDER" />
                        <p className="text-[10px] text-white/20 mt-1">{(data.sqlViewZI || "").length}/16 caracteres</p>
                    </div>
                </div>
            </div>

            <div className="bg-violet-500/5 border border-violet-500/20 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 rounded-lg bg-violet-600/30 border border-violet-500/40 flex items-center justify-center">
                        <span className="text-[10px] font-black text-violet-400">ZC</span>
                    </div>
                    <p className="text-[12px] font-black text-violet-400 uppercase tracking-widest">Consumption View</p>
                    <Badge color="violet">VDM #CONSUMPTION</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label>Nombre CDS (ZC_)</Label>
                        <Input value={data.zcName} onChange={e => setData(prev => ({ ...prev, zcName: e.target.value.toUpperCase() }))} placeholder="ZC_SalesOrder" />
                    </div>
                    <div>
                        <Label>SQL View Name (max 16 chars)</Label>
                        <Input value={data.sqlViewZC} onChange={e => setData(prev => ({ ...prev, sqlViewZC: e.target.value.toUpperCase().substring(0, 16) }))} placeholder="ZCSALESORDER" />
                        <p className="text-[10px] text-white/20 mt-1">{(data.sqlViewZC || "").length}/16 caracteres</p>
                    </div>
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-3">
                    <Label>Associations</Label>
                    <button onClick={addAssociation} className="flex items-center gap-1.5 text-[10px] font-black text-blue-400 hover:text-blue-300 uppercase tracking-widest transition-colors">
                        <Plus size={12} /> Agregar
                    </button>
                </div>
                {data.associations.length === 0 ? (
                    <div className="border border-dashed border-white/10 rounded-xl p-6 text-center">
                        <p className="text-[11px] text-white/20">Sin associations — opcionales</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {data.associations.map((a, i) => (
                            <div key={i} className="bg-black/30 border border-white/8 rounded-xl p-4 space-y-3">
                                <div className="grid grid-cols-3 gap-2">
                                    <div><Label>Nombre (_Xxx)</Label><Input value={a.name} onChange={e => updateAssoc(i, "name", e.target.value)} placeholder="_Customer" /></div>
                                    <div><Label>CDS Target</Label><Input value={a.target} onChange={e => updateAssoc(i, "target", e.target.value)} placeholder="ZI_Customer" /></div>
                                    <div><Label>Cardinalidad</Label><CardinalidadSelect value={a.cardinality} onChange={val => updateAssoc(i, "cardinality", val)} /></div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div><Label>Campo Local (ON)</Label><Input value={a.localField} onChange={e => updateAssoc(i, "localField", e.target.value)} placeholder="CustomerId" /></div>
                                    <div><Label>Campo Target (ON)</Label><Input value={a.targetField} onChange={e => updateAssoc(i, "targetField", e.target.value)} placeholder="CustomerId" /></div>
                                </div>
                                <button onClick={() => setData(prev => ({ ...prev, associations: prev.associations.filter((_, j) => j !== i) }))}
                                    className="text-[10px] text-white/20 hover:text-rose-400 flex items-center gap-1 transition-colors">
                                    <Trash2 size={11} /> Eliminar
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {/* Draft CDS View */}
            {
                (data.draftEnabled || data.template === "draft") && (
                    <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-6 h-6 rounded-lg bg-amber-600/30 border border-amber-500/40 flex items-center justify-center">
                                <span className="text-[10px] font-black text-amber-400">ZD</span>
                            </div>
                            <p className="text-[12px] font-black text-amber-400 uppercase tracking-widest">Draft CDS View</p>
                            <Badge color="amber">Draft-Enabled</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                            <div>
                                <Label>Nombre CDS Draft</Label>
                                <Input
                                    value={data.draftViewName || (data.ziName ? `${data.ziName}_D` : "")}
                                    onChange={e => setData(prev => ({ ...prev, draftViewName: e.target.value.toUpperCase() }))}
                                    placeholder="ZI_SALESORDER_D"
                                />
                            </div>
                            <div>
                                <Label>Draft Table (SE11)</Label>
                                <Input
                                    value={data.zDraftTableName || ""}
                                    onChange={e => setData(prev => ({ ...prev, zDraftTableName: e.target.value.toUpperCase() }))}
                                    placeholder="ZSALESORDER_D"
                                />
                            </div>
                        </div>

                        {/* Campos obligatorios del framework RAP */}
                        <div>
                            <p className="text-[10px] font-black text-amber-400/70 uppercase tracking-widest mb-2 flex items-center gap-2">
                                <Database size={11} /> Campos obligatorios RAP Framework
                            </p>
                            <div className="bg-black/30 border border-amber-500/15 rounded-xl overflow-hidden">
                                {[
                                    { name: "DraftUUID", type: "sysuuid", desc: "UUID del borrador — clave primaria", mandatory: true },
                                    { name: "ParentDraftUUID", type: "sysuuid", desc: "UUID del borrador padre (composición)", mandatory: true },
                                    { name: "RootDraftUUID", type: "sysuuid", desc: "UUID del borrador raíz", mandatory: true },
                                    { name: "DraftEntityType", type: "char30", desc: "Tipo de entidad del borrador", mandatory: true },
                                    { name: "IsActiveEntity", type: "abap_bool", desc: "Indica si es la entidad activa", mandatory: true },
                                    { name: "HasActiveEntity", type: "abap_bool", desc: "Indica si tiene contraparte activa", mandatory: true },
                                    { name: "HasDraftEntity", type: "abap_bool", desc: "Indica si tiene borrador asociado", mandatory: true },
                                    { name: "DraftAdministrativeData_DraftUUID", type: "sysuuid", desc: "Referencia a datos admin del draft", mandatory: true },
                                    { name: "CreatedAt", type: "timestampl", desc: "Fecha/hora de creación", mandatory: false },
                                    { name: "CreatedBy", type: "syuname", desc: "Usuario que creó el registro", mandatory: false },
                                    { name: "LastChangedAt", type: "timestampl", desc: "Fecha/hora última modificación", mandatory: false },
                                    { name: "LastChangedBy", type: "syuname", desc: "Usuario que modificó el registro", mandatory: false },
                                    { name: "LocalLastChangedAt", type: "timestampl", desc: "Última modificación local", mandatory: false },
                                ].map((field, i) => (
                                    <div key={i} className={`flex items-center gap-3 px-4 py-2 ${i % 2 === 0 ? "bg-black/20" : ""} ${i < 8 ? "border-l-2 border-amber-500/40" : "border-l-2 border-white/10"}`}>
                                        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${field.mandatory ? "bg-amber-400" : "bg-white/20"}`} />
                                        <span className="text-[11px] font-black text-white font-mono w-48 shrink-0">{field.name}</span>
                                        <span className="text-[10px] text-blue-400 font-mono w-24 shrink-0">{field.type}</span>
                                        <span className="text-[10px] text-white/30 flex-1">{field.desc}</span>
                                        {field.mandatory && <Badge color="amber">RAP</Badge>}
                                    </div>
                                ))}
                            </div>
                            <p className="text-[10px] text-white/20 mt-2 italic">
                                🟡 Campos obligatorios del framework RAP — generados automáticamente en el DDL
                            </p>
                        </div>
                    </div>
                )
            }
        </div>
    );
};

const Step3 = ({ data, setData }) => {
    const addItem = (field) => {
        setData(prev => ({ ...prev, [field]: [...prev[field], { name: "" }] }));
    };
    const updateItem = (field, index, key, value) => {
        setData(prev => ({ ...prev, [field]: prev[field].map((item, i) => i === index ? { ...item, [key]: value } : item) }));
    };

    return (
        <div className="space-y-6">
            <div>
                <Label>Operaciones CRUD</Label>
                <div className="grid grid-cols-2 gap-3">
                    {Object.entries(data.operations).map(([op, enabled]) => (
                        <button key={op}
                            onClick={() => setData(prev => ({ ...prev, operations: { ...prev.operations, [op]: !prev.operations[op] } }))}
                            className={`p-3 rounded-xl border text-left transition-all flex items-center gap-3 ${enabled ? "bg-blue-600/15 border-blue-500/40" : "bg-black/20 border-white/8 hover:border-white/20"}`}>
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${enabled ? "bg-blue-500 border-blue-500" : "border-white/20"}`}>
                                {enabled && <Check size={10} className="text-white" />}
                            </div>
                            <span className={`text-[11px] font-black uppercase tracking-wide ${enabled ? "text-blue-400" : "text-white/40"}`}>{op.toUpperCase()}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4">
                <Toggle checked={data.draftEnabled} onChange={val => setData(prev => ({ ...prev, draftEnabled: val }))} label="Draft Handling — Soporte para borradores" />
                {data.draftEnabled && (
                    <div className="mt-3 grid grid-cols-3 gap-2">
                        {["activate", "discard", "edit"].map(action => (
                            <div key={action} className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-center">
                                <p className="text-[10px] font-black text-amber-400 uppercase">{action}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {["actions", "validations"].map(field => (
                <div key={field}>
                    <div className="flex items-center justify-between mb-3">
                        <Label>{field === "actions" ? "Actions Personalizadas" : "Validaciones"}</Label>
                        <button onClick={() => addItem(field)} className="flex items-center gap-1.5 text-[10px] font-black text-blue-400 uppercase tracking-widest">
                            <Plus size={12} /> Agregar
                        </button>
                    </div>
                    {data[field].length === 0 ? (
                        <div className="border border-dashed border-white/10 rounded-xl p-4 text-center">
                            <p className="text-[11px] text-white/20">Sin {field} — opcionales</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {data[field].map((item, i) => (
                                <div key={i} className="flex gap-2">
                                    <Input value={item.name} onChange={e => updateItem(field, i, "name", e.target.value)} placeholder={field === "actions" ? "approveOrder" : "validateAmount"} />
                                    <button onClick={() => setData(prev => ({ ...prev, [field]: prev[field].filter((_, j) => j !== i) }))} className="text-white/20 hover:text-rose-400">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

const Step4 = ({ data, setData }) => (
    <div className="space-y-6">
        <div>
            <Label>Tipo de Service Binding</Label>
            <div className="grid grid-cols-2 gap-3">
                {BINDING_TYPES.map(bt => (
                    <button key={bt.id}
                        onClick={() => setData(prev => ({ ...prev, bindingType: bt.id, serviceBindingName: prev.entityName ? `ZSB_${prev.entityName}_${bt.id.toUpperCase()}` : prev.serviceBindingName }))}
                        className={`p-4 rounded-2xl border text-left transition-all ${data.bindingType === bt.id ? bt.color === "emerald" ? "bg-emerald-600/20 border-emerald-500/60" : "bg-amber-600/20 border-amber-500/60" : "bg-black/20 border-white/8 hover:border-white/20"}`}>
                        <div className="flex items-center gap-2 mb-1">
                            <p className={`text-[12px] font-black uppercase ${data.bindingType === bt.id ? bt.color === "emerald" ? "text-emerald-400" : "text-amber-400" : "text-white"}`}>{bt.label}</p>
                            <Badge color={bt.color}>{bt.badge}</Badge>
                        </div>
                        {data.bindingType === bt.id && <div className={`text-[8px] font-black uppercase tracking-widest mt-1 ${bt.color === "emerald" ? "text-emerald-500" : "text-amber-500"}`}>● ACTIVO</div>}
                    </button>
                ))}
            </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
            <div>
                <Label>Service Definition Name</Label>
                <Input value={data.serviceDefName} onChange={e => setData(prev => ({ ...prev, serviceDefName: e.target.value.toUpperCase() }))} placeholder="ZSD_SALESORDER" />
            </div>
            <div>
                <Label>Service Binding Name</Label>
                <Input value={data.serviceBindingName} onChange={e => setData(prev => ({ ...prev, serviceBindingName: e.target.value.toUpperCase() }))} placeholder="ZSB_SALESORDER_V4" />
            </div>
        </div>

        <div className="bg-white/3 border border-white/8 rounded-2xl p-4">
            <Toggle checked={data.exposeAssociations} onChange={val => setData(prev => ({ ...prev, exposeAssociations: val }))} label="Exponer Associations en el Service" />
        </div>

        <div className="bg-gradient-to-br from-blue-600/10 to-violet-600/10 border border-blue-500/20 rounded-2xl p-5">
            <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                <Zap size={12} /> Resumen — Objetos a generar
            </p>
            <div className="space-y-2">
                {[
                    { label: "Tabla Z Principal", value: data.zTableName || "Z...", color: "amber" },
                    data.draftEnabled && { label: "Tabla Z Draft", value: data.zDraftTableName || "Z..._D", color: "amber" },
                    { label: "Interface View", value: data.ziName || "ZI_...", color: "blue" },
                    { label: "Consumption View", value: data.zcName || "ZC_...", color: "violet" },
                    { label: "Behavior Definition", value: data.zrName || (data.entityName ? `ZR_${data.entityName}` : "ZR_..."), color: "emerald" },
                    { label: "Behavior Implementation", value: data.entityName ? `ZBP_R_${data.entityName}` : "ZBP_R_...", color: "emerald" },
                    { label: "├─ CLASS create", value: data.entityName ? `lhc_${data.entityName}` : "lhc_...", color: "emerald" },
                    { label: "├─ CLASS update", value: data.entityName ? `lhc_${data.entityName}` : "lhc_...", color: "emerald" },
                    { label: "├─ CLASS delete", value: data.entityName ? `lhc_${data.entityName}` : "lhc_...", color: "emerald" },
                    { label: "├─ CLASS draft", value: data.draftEnabled && data.entityName ? `lhc_${data.entityName}_D` : null, color: "amber" },
                    data.draftEnabled && { label: "├─ CLASS draft", value: data.entityName ? `lhc_${data.entityName}_D` : "lhc_..._D", color: "amber" },
                    ...data.actions.map(a => ({ label: `├─ ACTION ${a.name || "..."}`, value: `METHOD ${a.name || "..."}`, color: "violet" })),
                    ...data.validations.map(v => ({ label: `├─ VALIDATE ${v.name || "..."}`, value: `METHOD ${v.name || "..."}`, color: "rose" })),
                    { label: "Service Definition", value: data.serviceDefName || "ZSD_...", color: "amber" },
                    { label: "Service Binding", value: data.serviceBindingName || "ZSB_...", color: "amber" },
                ].filter(Boolean).map((item, i) => (
                    <div key={i} className="flex items-center justify-between">
                        <span className="text-[11px] text-white/40">{item.label}</span>
                        <span className={`text-[11px] font-black font-mono text-${item.color}-400`}>{item.value}</span>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const RapWizard = ({ data: rawData, setData }) => {
    const data = {
        ...initialState,
        ...rawData,
        keyFields: rawData?.keyFields?.length ? rawData.keyFields : initialState.keyFields,
        dataFields: rawData?.dataFields?.length ? rawData.dataFields : initialState.dataFields,
        operations: rawData?.operations || initialState.operations,
        associations: rawData?.associations || initialState.associations,
        actions: rawData?.actions || initialState.actions,
        validations: rawData?.validations || initialState.validations,
        determinations: rawData?.determinations || initialState.determinations,
    };
    const [currentStep, setCurrentStep] = useState(0);

    const steps = [
        <Step1 key={0} data={data} setData={setData} />,
        <Step2 key={1} data={data} setData={setData} />,
        <Step3 key={2} data={data} setData={setData} />,
        <Step4 key={3} data={data} setData={setData} />,
    ];

    return (
        <div className="bg-white/3 border border-white/8 rounded-[28px] overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600/15 to-violet-600/10 border-b border-white/8 p-5">
                <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                        <Code2 size={16} className="text-white" />
                    </div>
                    <div>
                        <p className="text-[13px] font-black text-white uppercase tracking-wide leading-none">RAP Architect</p>
                        <p className="text-[10px] text-blue-400 font-bold uppercase tracking-widest mt-0.5">RESTful Application Programming · S/4HANA Cloud</p>
                    </div>
                    <div className="ml-auto"><Badge color="blue">S/4HANA Cloud</Badge></div>
                </div>

                <div className="flex items-center gap-0">
                    {STEP_LABELS.map((label, i) => {
                        const Icon = STEP_ICONS[i];
                        const isActive = i === currentStep;
                        const isDone = i < currentStep;
                        return (
                            <React.Fragment key={i}>
                                <button onClick={() => setCurrentStep(i)}
                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${isActive ? "bg-blue-600 shadow-lg shadow-blue-500/30" : isDone ? "bg-blue-600/20 hover:bg-blue-600/30" : "bg-white/5 hover:bg-white/10"}`}>
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${isActive ? "bg-white/20" : isDone ? "bg-blue-500/40" : "bg-white/10"}`}>
                                        {isDone ? <Check size={10} className="text-blue-400" /> : <Icon size={10} className={isActive ? "text-white" : "text-white/40"} />}
                                    </div>
                                    <div className="text-left hidden sm:block">
                                        <p className={`text-[10px] font-black uppercase leading-none ${isActive ? "text-white" : isDone ? "text-blue-400" : "text-white/30"}`}>{label}</p>
                                        <p className={`text-[9px] leading-none mt-0.5 ${isActive ? "text-white/60" : "text-white/20"}`}>{STEP_SUBLABELS[i]}</p>
                                    </div>
                                </button>
                                {i < STEP_LABELS.length - 1 && <ChevronRight size={14} className={`shrink-0 mx-1 ${i < currentStep ? "text-blue-400" : "text-white/15"}`} />}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            <div className="p-6">{steps[currentStep]}</div>

            <div className="border-t border-white/8 px-6 py-4 flex items-center justify-between bg-black/20">
                <button onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))} disabled={currentStep === 0}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest text-white/40 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed transition-all">
                    <ChevronLeft size={14} /> Anterior
                </button>
                <div className="flex gap-1.5">
                    {STEP_LABELS.map((_, i) => (
                        <button key={i} onClick={() => setCurrentStep(i)}
                            className={`h-2 rounded-full transition-all ${i === currentStep ? "bg-blue-500 w-6" : i < currentStep ? "bg-blue-500/40 w-2" : "bg-white/15 w-2"}`} />
                    ))}
                </div>
                <button onClick={() => setCurrentStep(prev => Math.min(STEP_LABELS.length - 1, prev + 1))} disabled={currentStep === STEP_LABELS.length - 1}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-20 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/20">
                    Siguiente <ChevronRight size={14} />
                </button>
            </div>
        </div>
    );
};

export default RapWizard;